// Die Stationsübersicht einer Besichtigung (Mockup /doku/aufnahme): der Weg
// durchs Haus, sieben Stationen vom Dach bis zum Verteiler. Was erledigt
// ist, hat einen Haken; was jetzt dran ist, ist hervorgehoben. Gestrichelt,
// mit „später“: Drohnenfoto (Stufe 2) und Termin (Kiste Termin). Am Ende
// die Dachplanung mit K2 (seit Schritt 3) – PDF ablegen oder überspringen.
// Oben Name und Adresse: antippen berichtigt sie (seit 19.10.2026).
//
// Seit Schritt 4: Sind alle Stationen fertig und ist zur Dachplanung etwas
// gesagt, führt der Fuß zu „Abschließen und senden“. Ist die Besichtigung
// gesendet, ist dies die Einsicht: oben „im Büro“ (oder „wird gesendet“),
// statt des Fortschritts wann gesendet, die Stationen nur zum Lesen, kein
// „ändern“.
//
// Seit Schritt 5 „Etwas nachreichen“: Es öffnet einen Nachtrag, und
// dieselbe Übersicht zeigt ihn – oben deutlich, dass er läuft, je Station
// kurz, was darin dazukam, unten „Nachtrag senden“ (zur Zusammenfassung,
// NachtragSeite.tsx) und, solange er leer ist, „Nachtrag abbrechen“. Wird
// er gesendet, steht da „wird gesendet“ wie bei der ersten Sendung. Seit
// E3c (S5; ANFORDERUNGEN.md H25) steht der Knopf OBEN – vorher war er unten
// im Fuß, und Manuel fand ihn nicht; er geht wie bisher auch nach der
// Freigabe. Unten steht dann „Zur Startseite“.
//
// Seit E3 (S6, S7), wenn die Besichtigung gesendet ist: oben das Band „1
// offene Frage aus dem Büro – antworten“ (Gernots Entscheidung 2: auch in
// der Besichtigung), darunter die Fragen an der Besichtigung selbst (und
// die, deren Stück das Gerät nicht kennt – keine fehlt still); an jeder
// Station, wo noch Fragen offen sind; im Weg durchs Haus statt des
// Platzhalters „Termin ausmachen“ der Montagetermin, darunter zum Eintragen
// und Ändern (termin.tsx), und „Frage stellen“ zur ganzen Besichtigung.
// Beim Öffnen frischt die Seite Fragen und Termin vom Server auf
// (einsicht.ts, `auffrischenAnstossen`).
//
// Seit E3b (O65): Nach Station 7 die Zeile „Material“ – freiwillig, ohne
// „fertig“; sie zählt, was eingetragen ist (auch aus den Stationen), und
// führt zur Seite „Material“ am Schluss. An jeder Station steht „Material“,
// wenn von dort welches kam.
//
// Hier stehen auch, was die Seiten der Besichtigung gemeinsam lesen
// (`aufnahmeLesen`) und wie sie den Nachtrag zeigen.

import { useEffect, useRef, useState } from "react";
import { dachFaktenDa, dachHoeheText, STATIONEN, type IchAntwort } from "@happy-energy/kern";
import { AppFuss, AppKopf, Chip, fehlerMelden, Ikon, Ring, Tipp, toastZeigen } from "@happy-energy/oberflaeche";
import { speicher, type Aufnahme, type Foto, type K2, type Laenge, type Materialangabe, type Memo, type Nachtrag, type Notiz, type Station } from "../speicher.ts";
import { erneutVersuchen, nachtragLeer, nachtragStuecke, STATIONEN_ZAHL, stationenFertig, type NachtragStuecke } from "../postausgang.ts";
import {
  bearbeiten,
  besichtigungVerwerfen,
  kundeAenderbar,
  meterZeigen,
  nachtragBeginnen,
  nachtragUnterwegs,
  nachtragVerwerfen,
  type Bearbeiten,
} from "../arbeitskopie.ts";
import { auffrischenAnstossen, KOPIE_FRIST_MS, wannZeigen } from "../einsicht.ts";
import { groesseZeigen } from "../k2.ts";
import { fragenAmGeraet, frageOrt, gespraechMoeglich, obenAnDerBesichtigung, terminAmGeraet, wartetAufMich, type FrageAmGeraet } from "../gespraech.ts";
import { anzahl, SendeStand, useLive, usePostausgang, ZUM_START } from "./gemeinsam.tsx";
import { FrageMarke, FragenAbschnitt, FragenBand, FrageStellen, offeneAn } from "./fragen.tsx";
import { useUebernahmen } from "./fotos.tsx";
import { fortschrittText as uebernahmeText, uebernahmeStand } from "../vom-geraet.ts";
import { MontageterminKarte, terminZeile } from "./termin.tsx";

export interface AufnahmeGanz {
  aufnahme: Aufnahme;
  stationen: Station[];
  notizen: Notiz[];
  laengen: Laenge[];
  /** Das Material von draußen (seit E3b) – in der Reihenfolge, in der es dazukam. */
  materialangaben: Materialangabe[];
  /** Alle Fotos der Besichtigung, je Station in ihrer Reihenfolge. */
  fotos: Foto[];
  /** Alle Sprachmemos, in der Reihenfolge der Aufnahme – auch eines, das gerade aufgenommen wird (ohne Prüfsumme). */
  memos: Memo[];
  /** Die Dachplanung mit K2 – null, solange weder PDF noch „übersprungen“. */
  k2: K2 | null;
  /** „Etwas nachreichen“ (seit Schritt 5) – ältester zuerst. */
  nachtraege: Nachtrag[];
}

export async function aufnahmeLesen(id: string): Promise<AufnahmeGanz | null> {
  const aufnahme = await speicher.aufnahmen.get(id);
  if (!aufnahme) return null;
  const [stationen, notizen, laengen, materialangaben, fotos, memos, k2, nachtraege] = await Promise.all([
    speicher.stationen.where("aufnahmeId").equals(id).sortBy("nr"),
    speicher.notizen.where("aufnahmeId").equals(id).toArray(),
    speicher.laengen.where("aufnahmeId").equals(id).toArray(),
    speicher.materialangaben.where("aufnahmeId").equals(id).sortBy("nr"),
    speicher.fotos.where("aufnahmeId").equals(id).sortBy("reihenfolge"),
    speicher.memos.where("aufnahmeId").equals(id).sortBy("aufgenommenAm"),
    speicher.k2.get(aufnahme.anlage.id),
    speicher.nachtraege.where("aufnahmeId").equals(id).sortBy("begonnenAm"),
  ]);
  return { aufnahme, stationen, notizen, laengen, materialangaben, fotos, memos, k2: k2 ?? null, nachtraege };
}

/** Wie sich die Besichtigung gerade bearbeiten lässt: aufnehmen, nachreichen oder nur ansehen (arbeitskopie.ts). */
export function modusVon(ganz: Pick<AufnahmeGanz, "aufnahme" | "nachtraege">): Bearbeiten {
  return bearbeiten(ganz.aufnahme, ganz.nachtraege);
}

/** Die fertigen Memos an einer Notiz (Station oder Foto) – leer, wenn es die Notiz noch nicht gibt. */
export function memosZurNotiz(ganz: AufnahmeGanz, notiz: Notiz | undefined): Memo[] {
  return notiz ? ganz.memos.filter((m) => m.notizId === notiz.id && m.pruefsumme !== null) : [];
}

/** Ist zur Dachplanung etwas gesagt – PDF oder „übersprungen“? */
export function k2Erledigt(k2: K2 | null): boolean {
  return k2 !== null && (k2.uebersprungen || k2.pruefsumme !== null);
}

/** Die erste Station, die noch nicht fertig ist – „jetzt dran“. */
export function jetztDran(stationen: readonly Station[]): Station | undefined {
  return stationen.find((s) => !s.fertig);
}

/** Was an einer Station schon aufgenommen ist, kurz: „3 Fotos · Notiz · 2 Memos · 12 m“. */
function stationsInfo(s: Station, ganz: AufnahmeGanz): string[] {
  const teile: string[] = [];
  const fotos = ganz.fotos.filter((f) => f.stationId === s.id);
  if (fotos.length > 0) teile.push(anzahl(fotos.length, "1 Foto", "# Fotos"));
  if (ganz.notizen.some((n) => n.stationId === s.id && n.text.trim() !== "")) teile.push("Notiz");
  // Memos an der Notiz der Station und an den Notizen ihrer Fotos.
  const fotoIds = new Set(fotos.map((f) => f.id));
  const notizIds = new Set(ganz.notizen.filter((n) => n.stationId === s.id || (n.fotoId !== null && fotoIds.has(n.fotoId))).map((n) => n.id));
  const memos = ganz.memos.filter((m) => m.pruefsumme !== null && notizIds.has(m.notizId)).length;
  if (memos > 0) teile.push(anzahl(memos, "1 Memo", "# Memos"));
  const meter = ganz.laengen.filter((l) => l.stationId === s.id && l.meter !== null).reduce((summe, l) => summe + (l.meter ?? 0), 0);
  if (meter > 0) teile.push(`${meterZeigen(Math.round(meter * 100) / 100)} m`);
  if (ganz.materialangaben.some((m) => m.stationId === s.id)) teile.push("Material");
  // Seit E3c (S5): an der Station Dach Höhe und Zugang für die Dachpartie.
  const dach = s.art === "dach" ? (s.fachfragen ?? null) : null;
  if (dachFaktenDa(dach)) teile.push(dach.hoehe_m !== undefined ? `${dachHoeheText(dach.hoehe_m)} hoch` : "für die Dachpartie");
  if (s.sonstiges?.trim()) teile.push("Sonstiges");
  return teile;
}

/**
 * Die Zeile „Material“ im Weg (seit E3b): wie viel eingetragen ist – „3
 * Posten, 2 davon aus den Stationen“ –, sonst, dass es freiwillig ist.
 */
export function materialZeile(material: readonly Pick<Materialangabe, "stationId">[], nurLesen: boolean): string {
  if (material.length === 0) return nurLesen ? "keines angegeben" : "freiwillig – was du schon weißt";
  const ausStationen = material.filter((m) => m.stationId !== null).length;
  const posten = anzahl(material.length, "1 Posten", "# Posten");
  if (ausStationen === 0) return posten;
  if (ausStationen === material.length) return `${posten}, ${material.length === 1 ? "aus einer Station" : "aus den Stationen"}`;
  return `${posten}, ${ausStationen} davon aus den Stationen`;
}

/**
 * Was ein Nachtrag an einer Station gebracht hat, kurz: „2 Fotos · Notiz ·
 * Länge berichtigt“ – leer, wenn dort nichts dazukam. Ohne Station: für
 * die ganze Besichtigung.
 */
export function nachtragInfo(stuecke: NachtragStuecke, nachtragId: string, stationId?: string): string[] {
  const hier = (x: { stationId: string | null }) => stationId === undefined || x.stationId === stationId;
  const fotos = stuecke.fotos.filter(hier);
  const fotoIds = new Set(fotos.map((f) => f.id));
  const notizen = stuecke.notizen.filter((n) => hier(n) || (n.fotoId !== null && fotoIds.has(n.fotoId)));
  const notizIds = new Set(notizen.map((n) => n.id));
  const memos = stuecke.memos.filter((m) => notizIds.has(m.notizId)).length;
  const zurStation = notizen.filter((n) => n.stationId !== null && n.text.trim() !== "").length;
  const laengen = stuecke.laengen.filter(hier);
  const neu = laengen.filter((l) => l.nachtragId === nachtragId).length;
  const berichtigt = laengen.length - neu;
  const berichtigteStationen = stuecke.stationen.filter((s) => stationId === undefined || s.id === stationId);
  const sonstiges = berichtigteStationen.length;
  // Seit E3c (S5): An der Station Dach kann es auch Höhe oder Zugang sein – dieselbe Marke.
  const amDach = stationId !== undefined && berichtigteStationen.some((s) => s.art === "dach");
  return [
    fotos.length > 0 ? anzahl(fotos.length, "1 Foto", "# Fotos") : "",
    zurStation > 0 ? anzahl(zurStation, "Notiz", "# Notizen") : "",
    memos > 0 ? anzahl(memos, "1 Memo", "# Memos") : "",
    neu > 0 ? anzahl(neu, "neue Länge", "# neue Längen") : "",
    berichtigt > 0 ? anzahl(berichtigt, "Länge berichtigt", "# Längen berichtigt") : "",
    // Material (seit E3b): an einer Station nur ihres; für die ganze Besichtigung alles.
    ...materialImNachtrag(stuecke.materialangaben.filter(hier), nachtragId),
    sonstiges > 0 ? (amDach ? "Höhe, Zugang oder Sonstiges berichtigt" : anzahl(sonstiges, "Sonstiges berichtigt", "Sonstiges an # Stationen berichtigt")) : "",
  ].filter(Boolean);
}

/** Was ein Nachtrag am Material gebracht hat: „2 Posten Material · Menge berichtigt“ – leer, wenn nichts. */
export function materialImNachtrag(material: readonly Pick<Materialangabe, "nachtragId">[], nachtragId: string): string[] {
  const neu = material.filter((m) => m.nachtragId === nachtragId).length;
  const berichtigt = material.length - neu;
  return [neu > 0 ? anzahl(neu, "1 Posten Material", "# Posten Material") : "", berichtigt > 0 ? anzahl(berichtigt, "Menge berichtigt", "# Mengen berichtigt") : ""].filter(
    Boolean,
  );
}

/** Wann, ganz: „Montag, 19. Oktober, 10:42“. */
export function zeitpunktGanz(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString("de-AT", { weekday: "long", day: "numeric", month: "long" })}, ${d.toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" })}`;
}

/**
 * Über Nachgereichtem: „Nachgereicht heute 10:42“, „Nachgereicht am 12.
 * Okt.“ – wann der Server ihn bestätigt hat (sonst wann gesendet oder
 * begonnen). Ein Nachtrag, den das Gerät nicht kennt: nur „Nachgereicht“.
 * `wort`: was davor steht, etwa klein mitten im Satz.
 */
export function nachgereichtAm(n: Nachtrag | undefined, wort = "Nachgereicht"): string {
  const wann = n ? wannZeigen(n.imBueroAm ?? n.abgeschlossenAm ?? n.begonnenAm) : "";
  if (!wann) return wort;
  return /^(heute|gestern)/.test(wann) ? `${wort} ${wann}` : `${wort} am ${wann}`;
}

/** Oben rechts in der Einsicht: wo die gesendete Besichtigung steht – samt einem Nachtrag, der gerade gesendet wird. */
export function GesendetChip({ aufnahme, nachtraege }: { aufnahme: Aufnahme; nachtraege: readonly Nachtrag[] }) {
  if (aufnahme.fehler) {
    return (
      <Chip art="fehler" ikon="achtung">
        Fehler
      </Chip>
    );
  }
  if (aufnahme.stand === "im_buero" && !nachtragUnterwegs(nachtraege)) {
    return (
      <Chip art="ok" ikon="haken">
        im Büro
      </Chip>
    );
  }
  return (
    <Chip art="dunkel" ikon="uhr">
      wird gesendet
    </Chip>
  );
}

function fortschrittText(fertig: number, naechste: Station | undefined, k2Fertig: boolean): { titel: string; text: string } {
  const name = STATIONEN.find((s) => s.nr === naechste?.nr)?.name;
  if (!naechste && !k2Fertig) return { titel: "Alle Stationen fertig", text: "Jetzt noch Material, wenn du schon etwas weißt, und die Dachplanung mit K2." };
  if (!naechste) return { titel: "Alles beisammen", text: "Jetzt abschließen und ans Büro senden." };
  if (fertig === 0) return { titel: "Los geht’s", text: `Sieben Stationen vom Dach bis zum Verteiler – zuerst: ${name}.` };
  if (fertig === STATIONEN_ZAHL - 1) return { titel: "Fast fertig", text: `Es fehlt nur noch: ${name}.` };
  return { titel: `Noch ${STATIONEN_ZAHL - fertig} Stationen`, text: `Als Nächstes: ${name}.` };
}

/** Die Karte oben, wenn die Besichtigung gesendet ist: im Büro, auf dem Weg, oder ein Nachtrag, der läuft oder gesendet wird. */
function GesendetKarte({ ganz, modus }: { ganz: AufnahmeGanz; modus: Bearbeiten }) {
  const { aufnahme, nachtraege } = ganz;
  const unterwegs = nachtragUnterwegs(nachtraege);
  const nachgereicht = nachtraege.filter((n) => n.stand === "im_buero");
  let zeichen: { ikon: "haken" | "uhr" | "plus"; art: "avatar-lime" | "avatar-dunkel" };
  let titel: string;
  let text: string;
  if (modus.art === "nachtrag") {
    const info = nachtragInfo(nachtragStuecke(ganz, modus.nachtragId), modus.nachtragId);
    zeichen = { ikon: "plus", art: "avatar-lime" };
    titel = "Etwas nachreichen";
    text = info.length > 0 ? `Bisher dazu: ${info.join(" · ")}.` : "Noch nichts dazu – öffne die Station, an der etwas fehlt.";
  } else if (aufnahme.stand !== "im_buero") {
    zeichen = { ikon: "uhr", art: "avatar-dunkel" };
    titel = "Wartet am Gerät";
    text = `Abgeschlossen am ${zeitpunktGanz(aufnahme.abgeschlossenAm)} – geht von selbst ans Büro, sobald Empfang da ist.`;
  } else if (unterwegs) {
    zeichen = { ikon: "uhr", art: "avatar-dunkel" };
    titel = "Nachtrag wird gesendet";
    text = `Gesendet am ${zeitpunktGanz(unterwegs.abgeschlossenAm ?? null)} – geht von selbst ans Büro, sobald Empfang da ist.`;
  } else {
    zeichen = { ikon: "haken", art: "avatar-lime" };
    titel = "Ist im Büro";
    const zuletzt = nachgereicht.at(-1);
    text = `Gesendet am ${zeitpunktGanz(aufnahme.imBueroAm ?? aufnahme.abgeschlossenAm)}${
      zuletzt ? `, ${nachgereichtAm(zuletzt, "zuletzt etwas nachgereicht")}` : ""
    } – hier zum Nachsehen.`;
  }
  return (
    <div className="fortschritt-karte">
      <span className={`avatar ${zeichen.art}`}>
        <Ikon name={zeichen.ikon} />
      </span>
      <div className="fortschritt-wort">
        <b>{titel}</b>
        <span>{text}</span>
      </div>
    </div>
  );
}

/**
 * „Besichtigung verwerfen“ (seit E3c, S6; O97) – leise, ganz unten, nur
 * solange aufgenommen wird: ein Satz zur Rückfrage, dann weg am Gerät und
 * (sobald Netz da ist) am Server, zurück zur Startseite. Was gesendet ist,
 * löscht nur das Büro.
 */
function Verwerfen({ aufnahmeId }: { aufnahmeId: string }) {
  const [nachfrage, setNachfrage] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const rueckfrage = useRef<HTMLDivElement>(null);
  // Seit S7: Die Rückfrage steht ganz unten – ohne Rollen lägen „Ja, verwerfen“ und „Abbrechen“ unter dem Knopf im Fuß.
  useEffect(() => {
    if (nachfrage) rueckfrage.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [nachfrage]);

  async function verwerfen() {
    setLaeuft(true);
    try {
      const ergebnis = await besichtigungVerwerfen(aufnahmeId);
      if (ergebnis === "verworfen") {
        toastZeigen("Besichtigung verworfen");
        window.location.hash = "#/";
      } else {
        toastZeigen("Schon gesendet – löschen kann sie nur das Büro.", "fehler");
      }
    } catch (fehler) {
      fehlerMelden("doku", fehler);
      toastZeigen("Konnte die Besichtigung nicht verwerfen – bitte noch einmal.", "fehler");
    } finally {
      setLaeuft(false);
    }
  }

  if (!nachfrage) {
    return (
      <button type="button" className="knopf-leise" onClick={() => setNachfrage(true)}>
        Besichtigung verwerfen
      </button>
    );
  }
  return (
    <div ref={rueckfrage}>
      <Tipp art="rot" ikon="achtung">
        <b>Wirklich verwerfen?</b> Alles von dieser Besichtigung ist dann weg – am Gerät und im Büro. Das lässt sich nicht rückgängig machen.
        <button type="button" className="tipp-knopf" disabled={laeuft} onClick={() => void verwerfen()}>
          Ja, verwerfen
        </button>
        <button type="button" className="tipp-knopf" disabled={laeuft} onClick={() => setNachfrage(false)}>
          Abbrechen
        </button>
      </Tipp>
    </div>
  );
}

/** Bei einer Frage oben an der Besichtigung: woran sie eigentlich hängt – an der Besichtigung selbst nichts. */
function woOben(f: FrageAmGeraet): string | undefined {
  if (f.bezug.tabelle === "aufnahme") return undefined;
  if (f.bezug.tabelle === "anlage") return "zum ganzen Projekt";
  return "an etwas, das auf diesem Gerät nicht gespeichert ist";
}

export function Uebersicht({ ich, aufnahmeId }: { ich: IchAntwort; aufnahmeId: string }) {
  const ganz = useLive(() => aufnahmeLesen(aufnahmeId), aufnahmeId);
  const post = usePostausgang();
  // Seit E3c (S5; O94): Fotos „Vom Gerät“, die noch übernommen werden – auch, wenn man die Station verlassen hat.
  const laufende = useUebernahmen();

  // Gesendet (seit E3): Fragen und Termin vom Server auffrischen – beim Öffnen und wenn das Netz zurückkommt.
  const gesendet = ganz ? gespraechMoeglich(ganz.aufnahme) : false;
  useEffect(() => {
    if (!gesendet) return;
    const anstossen = () => auffrischenAnstossen(aufnahmeId);
    anstossen();
    window.addEventListener("online", anstossen);
    return () => window.removeEventListener("online", anstossen);
  }, [aufnahmeId, gesendet]);

  if (ganz === undefined) return <div className="app-seite" />;
  if (ganz === null) {
    return (
      <div className="app-seite">
        <AppKopf umgebung={ich.umgebung} zurueck={ZUM_START} titel="Übersicht" gross="Nicht gefunden" />
        <main className="app-inhalt">
          <Tipp art="grau">Diese Besichtigung ist auf diesem Gerät nicht gespeichert.</Tipp>
        </main>
      </div>
    );
  }

  const { aufnahme, stationen, k2, nachtraege } = ganz;
  const modus = modusVon(ganz);
  // Gesendet: nur noch zum Nachsehen (die Dachplanung geht weiter) – außer, es wird nachgereicht.
  const nurLesen = modus.art !== "aufnahme";
  const nachtragId = modus.art === "nachtrag" ? modus.nachtragId : null;
  const stuecke = nachtragId ? nachtragStuecke(ganz, nachtragId) : null;
  const materialNeu = stuecke && nachtragId ? materialImNachtrag(stuecke.materialangaben, nachtragId) : [];
  const leer = nachtragId ? nachtragLeer(ganz, nachtragId) : false;
  // „Etwas nachreichen“ geht, wenn die Besichtigung im Büro ist und kein Nachtrag läuft oder gesendet wird.
  const nachreichenGeht = modus.art === "gesperrt" && aufnahme.stand === "im_buero" && !nachtragUnterwegs(nachtraege);
  const fertig = stationenFertig(stationen);
  const naechste = nurLesen ? undefined : jetztDran(stationen);
  const k2Fertig = k2Erledigt(k2);
  const fortschritt = fortschrittText(fertig, naechste, k2Fertig);
  const k2Adresse = `#/aufnahme/${aufnahme.id}/k2`;
  // Fragen und Termin (seit E3) – erst, wenn die Besichtigung gesendet ist.
  const ichKurz = { id: ich.nutzer.id, name: ich.nutzer.name };
  const fragen = gesendet ? fragenAmGeraet(aufnahme) : [];
  const anMir = fragen.filter((f) => wartetAufMich(f, ichKurz.id));
  const ersteAnMir = anMir[0];
  const oben = fragen.filter((f) => obenAnDerBesichtigung(ganz, aufnahme.id, f));
  const termin = terminAmGeraet(aufnahme);
  const fotosStand = uebernahmeStand(laufende, aufnahme.id);
  const fotosWo = STATIONEN.find((st) => st.nr === stationen.find((s) => laufende.some((u) => u.art === "vom-geraet" && u.stationId === s.id))?.nr)?.name;

  async function nachreichen() {
    try {
      const ergebnis = await nachtragBeginnen(aufnahme.id);
      if (ergebnis.art === "offen") toastZeigen("Nachtrag läuft");
      else if (ergebnis.art === "unterwegs") toastZeigen("Der letzte Nachtrag ist noch unterwegs – gleich danach geht der nächste.", "fehler");
    } catch (fehler) {
      fehlerMelden("doku", fehler);
      toastZeigen("Konnte den Nachtrag nicht beginnen – bitte noch einmal.", "fehler");
    }
  }

  async function abbrechen(id: string) {
    try {
      const ergebnis = await nachtragVerwerfen(id);
      if (ergebnis === "verworfen" || ergebnis === "geschlossen") toastZeigen("Nachtrag abgebrochen");
      else if (ergebnis === "nicht-leer") toastZeigen("Im Nachtrag ist schon etwas – das geht ans Büro mit „Nachtrag senden“.", "fehler");
    } catch (fehler) {
      fehlerMelden("doku", fehler);
      toastZeigen("Konnte den Nachtrag nicht abbrechen – bitte noch einmal.", "fehler");
    }
  }

  return (
    <div className="app-seite">
      <AppKopf
        umgebung={ich.umgebung}
        zurueck={ZUM_START}
        // „Übersicht“ wie auf den Knöpfen, die hierher führen (Gernot, Augentest 19.10.2026).
        titel="Übersicht"
        rechts={modus.art === "gesperrt" ? <GesendetChip aufnahme={aufnahme} nachtraege={nachtraege} /> : <SendeStand wartend={post.wartend} />}
        gross={aufnahme.kunde.name}
        unter={`${aufnahme.objekt.strasse}, ${aufnahme.objekt.plz} ${aufnahme.objekt.ort}`}
        // Name und Adresse berichtigen – solange die Besichtigung nicht abgeschlossen ist.
        aendern={kundeAenderbar(aufnahme) ? `#/aufnahme/${aufnahme.id}/kunde` : undefined}
      />
      <main className="app-inhalt">
        {ersteAnMir && (
          <FragenBand
            klein
            zahl={anMir.length}
            erste={{ frageId: ersteAnMir.id, ort: frageOrt(ganz, aufnahme.id, ersteAnMir.bezug) }}
            unter={`${anMir.length > 1 ? "Zuerst: " : ""}${frageOrt(ganz, aufnahme.id, ersteAnMir.bezug).wo}`}
          />
        )}

        {aufnahme.fehler && (
          <Tipp art="rot" ikon="achtung">
            <b>Nicht alles ist beim Büro angekommen.</b> {aufnahme.fehler} Am Gerät ist alles sicher gespeichert.
            <button type="button" className="tipp-knopf" onClick={() => void erneutVersuchen(aufnahme.id)}>
              Nochmal senden
            </button>
          </Tipp>
        )}

        {fotosStand && (
          <Tipp art="grau" ikon="bilder">
            <b>{uebernahmeText(fotosStand.fertig, fotosStand.gesamt)}</b>
            {fotosWo ? ` (${fotosWo})` : ""} – weiterarbeiten geht, senden erst danach.
          </Tipp>
        )}

        {nachtragId && (
          <Tipp art="gelb" ikon="plus">
            <b>Nachtrag läuft</b> – was du jetzt dazu gibst oder berichtigst, kommt im Büro als neu an. Was gesendet ist, bleibt, wie es ist.
          </Tipp>
        )}

        {/* Seit E3c (S5, H25): „Etwas nachreichen“ oben, wo man zuerst hinsieht – nicht mehr unten im Fuß. */}
        {nachreichenGeht && (
          <div className="nachreichen-oben">
            <button type="button" className="knopf-haupt knopf-block" onClick={() => void nachreichen()}>
              <Ikon name="plus" /> Etwas nachreichen
            </button>
            <p className="klein-text">
              Ein Foto, eine Notiz, Material oder die Höhe am Dach vergessen? Hier kommt es dazu – was schon im Büro ist, bleibt, wie es ist.
            </p>
          </div>
        )}

        {/* Der Server nennt sie nicht mehr (einsicht.ts): sagen, statt still zu löschen – die Kopie bleibt noch drei Tage. */}
        {aufnahme.vermisstSeit && (
          <Tipp art="grau" ikon="info">
            <b>Im Büro nicht mehr gefunden.</b> Diese Besichtigung steht dort nicht mehr in der Liste – vielleicht wurde sie gelöscht. Die Kopie
            am Gerät bleibt noch bis {wannZeigen(new Date(Date.parse(aufnahme.vermisstSeit) + KOPIE_FRIST_MS).toISOString())}.
          </Tipp>
        )}

        {nurLesen ? (
          <GesendetKarte ganz={ganz} modus={modus} />
        ) : (
          <div className="fortschritt-karte">
            <Ring wert={fertig} von={STATIONEN_ZAHL} />
            <div className="fortschritt-wort">
              <b>{fortschritt.titel}</b>
              <span>{fortschritt.text}</span>
            </div>
          </div>
        )}

        {/* Die Fragen an der Besichtigung selbst – oben, wo man zuerst hinsieht. */}
        <FragenAbschnitt fragen={oben} ich={ichKurz} aufnahmeId={aufnahme.id} titel="Fragen zur Besichtigung" woJeFrage={woOben} />

        <div className="stationsweg">
          <div className="station extra">
            <span className="station-knoten">
              <Ikon name="drohne" groesse="k" />
            </span>
            <span className="station-karte">
              <span className="station-wort">
                <b>Drohnenfoto vom Haus</b>
                {/* Seit O91 (27.10.2026) gehen Drohnenfotos schon über „Vom Gerät“ – der Platzhalter bleibt als Teil von
                    Manuels Ablauf sichtbar (Grundsatz 8), sagt aber, wo sie heute hinkommen (Gernot, Augentest 27.10.). */}
                <span>heute bei „Dach“ über „Vom Gerät“ – eigener Platz kommt mit Stufe 2</span>
              </span>
              <Chip art="sp" ikon="uhr">
                später
              </Chip>
            </span>
          </div>

          {stationen.map((s) => {
            const st = STATIONEN.find((x) => x.nr === s.nr);
            const aktiv = s.id === naechste?.id;
            const info = stationsInfo(s, ganz);
            const neu = stuecke && nachtragId ? nachtragInfo(stuecke, nachtragId, s.id) : [];
            // Offene Fragen an der Station, ihren Fotos und Notizen (seit E3).
            const offen = offeneAn(fragen, (f) => frageOrt(ganz, aufnahme.id, f.bezug).stationId === s.id);
            return (
              <a key={s.id} href={`#/aufnahme/${aufnahme.id}/station/${s.nr}`} className={`station${s.fertig ? " fertig" : ""}${aktiv ? " aktiv" : ""}`}>
                <span className="station-knoten">{s.fertig ? <Ikon name="haken" /> : s.nr}</span>
                <span className="station-karte">
                  <span className="station-wort">
                    <b>{st?.name ?? s.art}</b>
                    {aktiv ? (
                      <span className="jetzt">jetzt dran{info.length > 0 ? ` · ${info.join(" · ")}` : ""}</span>
                    ) : neu.length > 0 ? (
                      <span className="nachgereicht">neu: {neu.join(" · ")}</span>
                    ) : (
                      <span>{s.fertig ? ["fertig", ...info].join(" · ") : info.length > 0 ? ["begonnen", ...info].join(" · ") : st?.kurz}</span>
                    )}
                    {offen > 0 && (
                      <span className="fragen-hinweis">
                        <FrageMarke>{anzahl(offen, "1 offene Frage", "# offene Fragen")}</FrageMarke>
                      </span>
                    )}
                  </span>
                  <Ikon name="rechts" />
                </span>
              </a>
            );
          })}

          {/* Seit E3b: Material am Schluss – freiwillig, deshalb ohne Haken und nie „jetzt dran“. */}
          <a href={`#/aufnahme/${aufnahme.id}/material`} className="station station-material">
            <span className="station-knoten">
              <Ikon name="paket" groesse="k" />
            </span>
            <span className="station-karte">
              <span className="station-wort">
                <b>Material</b>
                {materialNeu.length > 0 ? (
                  <span className="nachgereicht">neu: {materialNeu.join(" · ")}</span>
                ) : (
                  <span>{materialZeile(ganz.materialangaben, nurLesen)}</span>
                )}
              </span>
              <Ikon name="rechts" />
            </span>
          </a>

          {gesendet ? (
            // Seit E3 (S7): der Montagetermin, mit dem Kunden vereinbart – antippen führt zum Feld unten.
            <button
              type="button"
              className={`station station-termin${termin.am || termin.text ? " fertig" : ""}`}
              onClick={() => document.getElementById("montagetermin")?.scrollIntoView({ block: "center", behavior: "smooth" })}
            >
              <span className="station-knoten">
                <Ikon name="kalender" groesse="k" />
              </span>
              <span className="station-karte">
                <span className="station-wort">
                  <b>Montagetermin</b>
                  <span>{terminZeile(termin) || "noch keiner – unten eintragen"}</span>
                </span>
                <Ikon name="rechts" />
              </span>
            </button>
          ) : (
            <div className="station extra">
              <span className="station-knoten">
                <Ikon name="kalender" groesse="k" />
              </span>
              <span className="station-karte">
                <span className="station-wort">
                  <b>Termin ausmachen</b>
                  <span>nach dem Senden – mit dem Kunden vereinbart</span>
                </span>
                <Chip art="sp" ikon="uhr">
                  später
                </Chip>
              </span>
            </div>
          )}
          <a href={k2Adresse} className={`station${k2Fertig ? " fertig" : ""}${!nurLesen && !naechste && !k2Fertig ? " aktiv" : ""}`}>
            <span className="station-knoten">{k2Fertig ? <Ikon name="haken" /> : "K2"}</span>
            <span className="station-karte">
              <span className="station-wort">
                <b>Dachplanung mit K2</b>
                {k2?.pruefsumme ? (
                  // Seit E3c (S5): dazu die Liste (Excel-Report) – oder dass sie noch fehlt.
                  <span>{k2.excel ? "Plan und Liste abgelegt" : `PDF abgelegt · ${groesseZeigen(k2.groesse)} · die Liste fehlt noch`}</span>
                ) : k2?.excel && !k2.uebersprungen ? (
                  <span>die Liste ist abgelegt – der Plan (PDF) fehlt noch</span>
                ) : k2?.uebersprungen ? (
                  <span>übersprungen – das PDF kann jederzeit nachkommen</span>
                ) : nurLesen ? (
                  <span>noch offen – das PDF kann auch jetzt noch kommen</span>
                ) : !naechste ? (
                  <span className="jetzt">jetzt dran · Plan und Liste ablegen oder überspringen</span>
                ) : (
                  <span>nach dem Termin – Plan und Liste ablegen oder überspringen</span>
                )}
              </span>
              <Ikon name="rechts" />
            </span>
          </a>
        </div>

        {gesendet && (
          <>
            <MontageterminKarte aufnahme={aufnahme} />
            <FrageStellen aufnahmeId={aufnahme.id} bezug={{ tabelle: "aufnahme", id: aufnahme.id }} ich={ichKurz} wo="zur ganzen Besichtigung" />
          </>
        )}

        {modus.art === "aufnahme" ? (
          <>
            <p className="klein-text" style={{ margin: "4px 2px 0" }}>
              Alles wird sofort am Gerät gespeichert, auch ohne Empfang, und geht von selbst ans Büro, sobald wieder Netz da ist.
            </p>
            {/* Senden geht immer – auch wenn eine Station nicht fertig werden kann; die Seite fragt dann nach (Gernot, 19.10.2026).
                Kleiner als „Weiter: …“, aber ein echter Knopf (Gernots Augentest, 20.10.2026: der Link wirkte billig). */}
            {(naechste || !k2Fertig) && (
              <a href={`#/aufnahme/${aufnahme.id}/senden`} className="knopf-rand knopf-zweit knopf-block">
                <Ikon name="senden" /> Abschließen und senden
              </a>
            )}
            {/* Seit E3c (S6): leise, ganz unten – nur, solange nichts gesendet ist. */}
            <Verwerfen aufnahmeId={aufnahme.id} />
          </>
        ) : nachtragId ? (
          <>
            <p className="klein-text" style={{ margin: "4px 2px 0" }}>
              Alles wird sofort am Gerät gespeichert, auch ohne Empfang. Beim Büro ist der Nachtrag, sobald du ihn sendest.
            </p>
            {leer && (
              <button type="button" className="knopf-leise" onClick={() => void abbrechen(nachtragId)}>
                Nachtrag abbrechen
              </button>
            )}
          </>
        ) : null}
      </main>
      <AppFuss>
        {nachtragId ? (
          <a href={`#/aufnahme/${aufnahme.id}/nachtrag`} className="knopf-haupt knopf-block">
            <Ikon name="senden" /> Nachtrag senden
          </a>
        ) : nurLesen ? (
          // Seit S7: Steht oben „Etwas nachreichen“, ist das der eine Hauptknopf – „Zur Startseite“ tritt zurück.
          <a href="#/" className={`${nachreichenGeht ? "knopf-rand" : "knopf-haupt"} knopf-block`}>
            <Ikon name="haus" /> Zur Startseite
          </a>
        ) : naechste ? (
          <a href={`#/aufnahme/${aufnahme.id}/station/${naechste.nr}`} className="knopf-haupt knopf-block">
            Weiter: {STATIONEN.find((s) => s.nr === naechste.nr)?.name} <Ikon name="rechts" />
          </a>
        ) : !k2Fertig ? (
          <a href={k2Adresse} className="knopf-haupt knopf-block">
            Weiter: Dachplanung mit K2 <Ikon name="rechts" />
          </a>
        ) : (
          <a href={`#/aufnahme/${aufnahme.id}/senden`} className="knopf-haupt knopf-block">
            <Ikon name="senden" /> Abschließen und senden
          </a>
        )}
      </AppFuss>
    </div>
  );
}
